// Surface-aware organization profile mock data.
//
// lib/maket/ holds the few seed rows that Maket (no-backend) branches of
// eagerly loaded or real-API route modules read synchronously. Keeping them out
// of mocks/ stops those modules from pulling the whole MSW fixture chunk into
// the production download (OPT-20261031-05). Goes away with the Maket
// retirement (OPT-20261031-08).
//
// Volkan direction 2026-05-23 (Settings 10-section expansion): Settings overlay
// header surfaces the active organization name + a sub-label describing its
// operational scope. Each surface renders against a karakter-driven profile —
// Volkan A1 admin platform, Sarah Johnson BO2 seller, Mehmet Üretim Ortağı SO2
// supply workshop.
//
// Shopify-pattern Organization page consumes the same record for business
// details / contact / address / store defaults cards.
//
// Mock-only · M3 Stage 4 SPEC.md Faz 1 (memberships + scope_family) +
// Faz 6 (workspace + resources expansion) backend ratify.

import type { SettingsSurface } from '../../features/settings/Settings'

type OrgBusinessDetails = {
  legal_name: string
  display_name: string
  entity_type: string
  registration_number: string
  tax_id: string
}

type OrgContact = {
  primary_email: string
  support_email: string
  phone: string
}

type OrgAddress = {
  line1: string
  line2: string
  city: string
  region: string
  postal_code: string
  country: string
}

type OrgDefaults = {
  currency: string
  region: string
  unit_system: 'metric' | 'imperial'
  timezone: string
  weight_unit: string
  language: string
}

type OrgProfile = {
  surface: SettingsSurface
  display_name: string
  scope_label: string
  scope_sublabel: string
  business_details: OrgBusinessDetails
  contact: OrgContact
  address: OrgAddress
  defaults: OrgDefaults
}

const ORG_PROFILES: Record<SettingsSurface, OrgProfile> = {
  admin: {
    surface: 'admin',
    display_name: 'PEXIFY ADMIN',
    scope_label: '3 surfaces',
    scope_sublabel: 'Platform owner · governance layer',
    business_details: {
      legal_name: 'Pexify Commerce Operations LLC',
      display_name: 'PEXIFY ADMIN',
      entity_type: 'Platform Owner (A1)',
      registration_number: 'DE-2026-PEX-0001',
      tax_id: 'EIN 88-7654321',
    },
    contact: {
      primary_email: 'volkan@pexify.net',
      support_email: 'ops@pexify.net',
      phone: '+1 (302) 555-0100',
    },
    address: {
      line1: '1209 Orange Street',
      line2: 'Wilmington Operations HQ',
      city: 'Wilmington',
      region: 'Delaware',
      postal_code: '19801',
      country: 'United States',
    },
    defaults: {
      currency: 'USD',
      region: 'United States',
      unit_system: 'imperial',
      timezone: 'America/New_York',
      weight_unit: 'oz',
      language: 'English',
    },
  },
  seller: {
    surface: 'seller',
    display_name: 'LeatherCraftLab',
    scope_label: '3 stores',
    scope_sublabel: 'Sarah Johnson (BO2) · Etsy + Shopify + Amazon',
    business_details: {
      legal_name: 'LeatherCraftLab LLC',
      display_name: 'LeatherCraftLab',
      entity_type: 'External Seller (BO2)',
      registration_number: 'TX-LLC-2024-44218',
      tax_id: 'EIN 47-2218901',
    },
    contact: {
      primary_email: 'sarah@leathercraftlab.com',
      support_email: 'hello@leathercraftlab.com',
      phone: '+1 (512) 555-0142',
    },
    address: {
      line1: '4801 South Congress Avenue',
      line2: 'Studio C',
      city: 'Austin',
      region: 'Texas',
      postal_code: '78745',
      country: 'United States',
    },
    defaults: {
      currency: 'USD',
      region: 'United States',
      unit_system: 'imperial',
      timezone: 'America/Chicago',
      weight_unit: 'oz',
      language: 'English',
    },
  },
  supply: {
    surface: 'supply',
    display_name: 'Bursa Ahşap Atölyesi',
    scope_label: '1 workshop',
    scope_sublabel: 'Mehmet (SO2) · production partner',
    business_details: {
      legal_name: 'Bursa Ahşap Üretim Ltd. Şti.',
      display_name: 'Bursa Ahşap Atölyesi',
      entity_type: 'Production Partner (SO2)',
      registration_number: 'TR-BURSA-2022-09817',
      tax_id: 'VKN 1112223334',
    },
    contact: {
      primary_email: 'mehmet@bursaahsap.com.tr',
      support_email: 'siparis@bursaahsap.com.tr',
      phone: '+90 224 555 0142',
    },
    address: {
      line1: 'Demirtaşpaşa Mah. Atatürk Cad. No: 47',
      line2: 'Osmangazi OSB Blok B',
      city: 'Bursa',
      region: 'Bursa',
      postal_code: '16245',
      country: 'Türkiye',
    },
    defaults: {
      currency: 'TRY',
      region: 'Türkiye',
      unit_system: 'metric',
      timezone: 'Europe/Istanbul',
      weight_unit: 'kg',
      language: 'Türkçe',
    },
  },
}

export function orgProfileFor(surface: SettingsSurface): OrgProfile {
  return ORG_PROFILES[surface]
}
